import { amortization, monthlyPI } from "@zillow/mortgage-calculations";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { LOAN_PROGRAMS, COMMON_PERCENT_CONFIG, COMMON_AMOUNT_CONFIG, LOAN_TERM_IN_MONTHS } from "../../utils/zhl-calculator.esm.js";
import { START_YEAR_OPTIONS, RFC_2822_MONTHS, VALIDATE_YEAR_OPTIONS, TERM_TO_YEAR } from "../AmortizationCalculator/AmortizationCalculator.utils.esm.js";
const loanTermOptions = [{
  value: LOAN_PROGRAMS.fixed30Year,
  label: "30-year fixed"
}, {
  value: LOAN_PROGRAMS.fixed15Year,
  label: "15-year fixed"
}, {
  value: LOAN_PROGRAMS.arm5,
  label: "5-year ARM"
}];
const loanTermConfig = {
  required: "Required",
  validate: {
    invalidProgram: (value) => {
      let error = null;
      if (Object.values(LOAN_PROGRAMS).includes(value) === false) {
        error = "Invalid loan program";
      }
      return error;
    }
  }
};
const loanStartYearConfig = {
  required: "Required",
  validate: {
    invalidProgram: (value) => {
      let error = null;
      if (VALIDATE_YEAR_OPTIONS.includes(parseInt(value)) === false) {
        error = "Invalid start year";
      }
      return error;
    }
  }
};
const loanStartMonthConfig = {
  required: "Required",
  validate: {
    invalidProgram: (value) => {
      let error = null;
      if (RFC_2822_MONTHS.includes(value) === false) {
        error = "Invalid start month";
      }
      return error;
    }
  }
};
const FieldConfig = {
  typeOneLoanAmount: {
    name: "typeOneLoanAmount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  typeOneLoanTerm: {
    name: "typeOneLoanTerm",
    options: loanTermOptions,
    config: loanTermConfig
  },
  typeOneInterestRate: {
    name: "typeOneInterestRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  typeOneLoanStartMonth: {
    name: "typeOneLoanStartMonth",
    options: RFC_2822_MONTHS.map((i) => ({
      value: i,
      label: i
    })),
    config: loanStartMonthConfig
  },
  typeOneLoanStartYear: {
    name: "typeOneLoanStartYear",
    options: START_YEAR_OPTIONS,
    config: loanStartYearConfig
  },
  typeTwoLoanAmount: {
    name: "typeTwoLoanAmount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  typeTwoLoanTerm: {
    name: "typeTwoLoanTerm",
    options: loanTermOptions,
    config: loanTermConfig
  },
  typeTwoInterestRate: {
    name: "typeTwoInterestRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  typeTwoLoanStartMonth: {
    name: "typeTwoLoanStartMonth",
    options: RFC_2822_MONTHS.map((i) => ({
      value: i,
      label: i
    })),
    config: loanStartMonthConfig
  },
  typeTwoLoanStartYear: {
    name: "typeTwoLoanStartYear",
    options: START_YEAR_OPTIONS,
    config: loanStartYearConfig
  },
  typeThreeLoanAmount: {
    name: "typeThreeLoanAmount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  typeThreeLoanTerm: {
    name: "typeThreeLoanTerm",
    options: loanTermOptions,
    config: loanTermConfig
  },
  typeThreeInterestRate: {
    name: "typeThreeInterestRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  typeThreeLoanStartMonth: {
    name: "typeThreeLoanStartMonth",
    options: RFC_2822_MONTHS.map((i) => ({
      value: i,
      label: i
    })),
    config: loanStartMonthConfig
  },
  typeThreeLoanStartYear: {
    name: "typeThreeLoanStartYear",
    options: START_YEAR_OPTIONS,
    config: loanStartYearConfig
  }
};
function useLastValidFormValues(form) {
  return useLastValidValues(form, getAmortizationValues);
}
function getAmortizationFieldValues(inputs) {
  const numYears = TERM_TO_YEAR[inputs.term];
  const payOffYear = +numYears + +inputs.loanStartYear;
  const amort = amortization(inputs.amount, inputs.rate, numYears * 12);
  const lastAmortizationElement = amort[amort.length - 1];
  if (!lastAmortizationElement) {
    throw new Error("Unable to amortization data from mortgage calculatios");
  }
  return {
    totalInterest: lastAmortizationElement.totalInterest,
    totalPrincipal: lastAmortizationElement.totalPrincipal,
    monthlyPayment: monthlyPI(inputs.amount, inputs.rate, LOAN_TERM_IN_MONTHS[inputs.term]),
    payOffDate: `${inputs.loanStartMonth}.  ${payOffYear}`
  };
}
function getAmortizationValues(lastValidFields) {
  const typeOneLoanInput = {
    term: lastValidFields.typeOneLoanTerm,
    amount: lastValidFields.typeOneLoanAmount,
    rate: lastValidFields.typeOneInterestRate,
    loanStartMonth: lastValidFields.typeOneLoanStartMonth,
    loanStartYear: lastValidFields.typeOneLoanStartYear
  };
  const typeOneCalculations = getAmortizationFieldValues(typeOneLoanInput);
  const typeTwoLoanInput = {
    term: lastValidFields.typeTwoLoanTerm,
    amount: lastValidFields.typeTwoLoanAmount,
    rate: lastValidFields.typeTwoInterestRate,
    loanStartMonth: lastValidFields.typeTwoLoanStartMonth,
    loanStartYear: lastValidFields.typeTwoLoanStartYear
  };
  const typeTwoCalculations = getAmortizationFieldValues(typeTwoLoanInput);
  const typeThreeLoanInput = {
    term: lastValidFields.typeThreeLoanTerm,
    amount: lastValidFields.typeThreeLoanAmount,
    rate: lastValidFields.typeThreeInterestRate,
    loanStartMonth: lastValidFields.typeThreeLoanStartMonth,
    loanStartYear: lastValidFields.typeThreeLoanStartYear
  };
  const typeThreeCalculations = getAmortizationFieldValues(typeThreeLoanInput);
  const values = {
    fields: lastValidFields,
    typeOneCalculations,
    typeTwoCalculations,
    typeThreeCalculations
  };
  return values;
}
const calculatorDisclaimerText = "The loan comparison calculator is intended to be used for illustrative purposes only. Actual available rates and monthly payment amounts are subject to your credit profile, market fluctuations, and other factors, including geography and loan characteristics. The estimates are based on information you provide, and does not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.";
export {
  FieldConfig,
  calculatorDisclaimerText,
  getAmortizationFieldValues,
  useLastValidFormValues
};
